import { Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { map, mergeMap, take } from "rxjs/operators";
import { AccountRelatedDTO } from "../accounts/Account";
import { AccountAppInstrumentationHandlerService } from "../handlers/account-app-instrumentation-handler.service";
import { InstrumentationHandlerService } from "../handlers/instrumentation-handler.service";

@Injectable({
  providedIn: "root",
})
export class InstrumentationService {
  constructor(
    private InstrumentationHandlerService: InstrumentationHandlerService,
    private AccountAppInstrumentationHandlerService: AccountAppInstrumentationHandlerService,
  ) {}

  getInstruments() {
    return this.InstrumentationHandlerService.getInstruments();
  }

  getAccountAppInstruments(masterAccountID: number, appID: number) {
    return this.AccountAppInstrumentationHandlerService.getAccountAppInstruments(
      masterAccountID,
      appID,
    );
  }

  getAccountAppInstrument(
    masterAccountID: number,
    appID: number,
    instrumentID: number,
    startDate: Date,
    endDate: Date,
    compareDays: number,
  ) {
    return this.AccountAppInstrumentationHandlerService.getAccountAppInstrument(
      masterAccountID,
      appID,
      instrumentID,
      startDate,
      endDate,
      compareDays,
    );
  }

  deleteAppInstrument(
    masterAccountID: number,
    appID: number,
    instrumentID: number,
  ) {
    return this.AccountAppInstrumentationHandlerService.deleteAppInstrument(
      masterAccountID,
      appID,
      instrumentID,
    );
  }

  createAppInstrument(
    masterAccountID: number,
    appID: number,
    appInstrumentDTO: any,
  ) {
    return this.AccountAppInstrumentationHandlerService.createAppInstrument(
      masterAccountID,
      appID,
      appInstrumentDTO,
    );
  }

  swapInstruments(
    masterAccountID: number,
    appID: number,
    newAppInstrument: any,
    oldAppInstrument: any,
  ) {
    const newAppInstrumentDTO = {
      instrument_id: newAppInstrument.id,
      sort_order: oldAppInstrument.sort_order,
    };

    return this.deleteAppInstrument(
      masterAccountID,
      appID,
      oldAppInstrument.id,
    ).pipe(
      mergeMap(() => {
        return this.createAppInstrument(
          masterAccountID,
          appID,
          newAppInstrumentDTO,
        );
      }),
    );
  }

  //Used to build out accountAppInstrumentObj for resolve
  getAccountAppInstrumentsObs(
    account: AccountRelatedDTO,
    availableInstruments: any,
    startDate: Date,
    endDate: Date,
    compareDays: number,
    fromResolve?: boolean,
  ) {
    return this.getAccountAppInstruments(account.id, account.apps[0].id).pipe(
      mergeMap((accountAppInstruments) => {
        if (!accountAppInstruments?.length && fromResolve) {
          const createAppInstrumentsObs = [];
          let count = 0;

          for (let instrument of availableInstruments) {
            const newAppInstrumentDTO = {
              instrument_id: instrument.id,
              sort_order: count,
            };

            createAppInstrumentsObs.push(
              this.createAppInstrument(
                account.id,
                account.apps[0].id,
                newAppInstrumentDTO,
              ),
            );
            count++;
          }

          return forkJoin(createAppInstrumentsObs).pipe(
            mergeMap(() => {
              return this.getAccountAppInstruments(
                account.id,
                account.apps[0].id,
              ).pipe(
                mergeMap((appInstrumentations) => {
                  const appInstrumentationObs = appInstrumentations.map(
                    (appInstrumentation) => {
                      return this.getAccountAppInstrument(
                        account.id,
                        account.apps[0].id,
                        appInstrumentation.id,
                        startDate,
                        endDate,
                        compareDays,
                      ).pipe(
                        map((instrumentInfo) => {
                          const lhs = appInstrumentation || {};
                          const rhs = instrumentInfo || {};
                          return { ...(lhs as any), ...(rhs as any) };
                        }),
                      );
                    },
                  );

                  return forkJoin(appInstrumentationObs).pipe(
                    map((appInstrumentations: any) => {
                      return { account, appInstrumentations };
                    }),
                    take(1),
                  );
                }),
              );
            }),
          );
        } else {
          const appInstrumentationObs = accountAppInstruments.length
            ? accountAppInstruments.map((appInstrumentation) => {
                return this.getAccountAppInstrument(
                  account.id,
                  account.apps[0].id,
                  appInstrumentation.id,
                  startDate,
                  endDate,
                  compareDays,
                ).pipe(
                  map((accountAppInstrument) => {
                    const lhs = appInstrumentation || {};
                    const rhs = accountAppInstrument || {};
                    return { ...(lhs as any), ...(rhs as any) };
                  }),
                  take(1),
                );
              })
            : of(null);
          return forkJoin(appInstrumentationObs).pipe(
            map((appInstrumentations: any) => {
              if (appInstrumentations[0] === null) {
                appInstrumentations = [];
              }
              return { account, appInstrumentations };
            }),
            take(1),
          );
        }
      }),
    );
  }
}
